<script setup lang="ts">
import { Question } from "../components/fields";
import Input from "../components/fields/Input.vue";
import { loginValidator as validator } from "../utils/validators";
import { tooltips } from "../tooltips/loginTooltips";
import useVuelidate from "@vuelidate/core";
import { LOGIN_KEY, ResponseCodeMap, ResponseCodes, SESSION_EXPIRY, useLoginStore } from "../stores/login";
import { sendMagicToken } from "../data/login";
import { v4 as uuidv4 } from "uuid";
import { ToastRenderer, useToast } from "../services/toast";
import { useRouter } from "vue-router";
import { onMounted } from "vue";
import { digestMessage } from "../utils/crypto";
import { useConfigStore } from "../stores/config";

const config = useConfigStore();
const loginStore = useLoginStore();
const { toast } = useToast();
const router = useRouter();
const v$ = useVuelidate(validator, loginStore);

function showToast(message: string) {
    toast({
        title: "Error",
        message,
        timeout: config.toastTimeout,
    });
}

onMounted(() => {
    loginStore.updateLoginState();
    if (loginStore.expiry > new Date().getTime()) {
        router.replace({ path: "/login/confirm" });
    }
});

async function handleLoginClick() {
    const valid = await v$.value.$validate();
    if (valid) {
        const codeVerifier = `${uuidv4()}${uuidv4()}`;
        const hex = (await digestMessage(codeVerifier)).toUpperCase();

        loginStore.codeVerifier = codeVerifier;
        sessionStorage.setItem(LOGIN_KEY, JSON.stringify({
            codeVerifier,
            email: loginStore.email,
            loginAttempts: loginStore.loginAttempts,
            expiry: SESSION_EXPIRY,
            code: loginStore.code,
            isLoggedIn: loginStore.isLoggedIn,
        }));

        try {
            const response = await sendMagicToken(loginStore.email, hex);
            const status = response.status;

            if (status === ResponseCodes.Ok) {
                router.push({ path: "/login/confirm" });
            }

            switch (true) {
                case (status === 429):
                    showToast(ResponseCodeMap[ResponseCodes.TooManyRequests]);
                    break;
                case (status === 400):
                    showToast(ResponseCodeMap[ResponseCodes.BadRequest]);
                    break;
                case (status > 400 && status < 500):
                    showToast(ResponseCodeMap[ResponseCodes.Problem]);
                    break;
                case (status === 500):
                    showToast(ResponseCodeMap[ResponseCodes.Error]);
                    break;
            }
        } catch {
            showToast(ResponseCodeMap[ResponseCodes.Error]);
        }
    }
}

</script>
<template>
    <div class="container col-lg-6 col-md-6 mx-auto login">
        <h1 class="mt-4">
            Login
        </h1>
        <h5 class="pt-3 pb-3 blue">
            To login to Annuity Ready, enter your email below.
        </h5>
        <p class="mb-3">
            We will email you a one-time verification code that you can use to login.
        </p>
        <Question
            name="email"
            label="Email Address"
            :tooltip="tooltips.email"
        >
            <Input
                id="login-email"
                v-model="loginStore.email"
                class="col-12"
                placeholder="Email"
                type="email"
                :validation="validator.email"
            />
        </Question>
        <button
            id="btn-login"
            class="btn btn-info btn-lg mt-2"
            @click="handleLoginClick()"
        >
            Request a one-time verification code
        </button>
        <hr>
        <p>
            Get a <router-link
                id="new-quote-link"
                to="/"
            >
                new quote
            </router-link>
        </p>
    </div>
    <ToastRenderer />
</template>
